/* ==========================================================================
   BLAST MASTER, Shopify PDP Prototype
   ==========================================================================
   Vanilla JS. No jQuery. addEventListener everywhere, no inline handlers.
   Each feature is its own IIFE so it can be ported into Shopify's section
   JavaScript model with minimal rewrites.
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Utility, wait for DOM
     -------------------------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initMobileNav();
    initGallery();
    initVideoRail();
    initFlashBar();
    initBundlePicker();
    initBundleProtection();
    initPurchaseType();
    initProblemSection();
    initLiveOrders();
    initReviewsScrollLink();
    initAccordions();
    initDeliveryDates();
    initReviewsCarousel();
    initStickyATC();
    initScrollTop();
    initATC();
    initCartCountSync();
    initNewsletter();
    initScratchPopup();
  }


  /* ==========================================================================
     Note: the announcement bar is a CSS-only marquee (see styles.css , 
     SECTION 1). No JS needed. Animation pauses automatically when the user
     has prefers-reduced-motion enabled.
     ========================================================================== */


  /* ==========================================================================
     Mobile nav, hamburger toggle
     ========================================================================== */
  function initMobileNav() {
    const btn = document.querySelector('.nav-hamburger');
    const drawer = document.querySelector('.mobile-drawer');
    if (!btn || !drawer) return;

    btn.addEventListener('click', () => {
      const isOpen = btn.classList.toggle('is-open');
      drawer.classList.toggle('is-open', isOpen);
      btn.setAttribute('aria-expanded', String(isOpen));
      drawer.setAttribute('aria-hidden', String(!isOpen));
    });
  }


  /* ==========================================================================
     Hero media, auto-rotating cross-fade ONLY
     ==========================================================================
     Multiple <img> elements sit absolute-stacked inside .hero-media__main;
     only the one with .is-visible is opaque (CSS handles the opacity
     transition). Rotation advances on a fixed 2s interval and cannot be
     stopped or steered by the user, no swipe, no dots, no thumbs. Users
     on `prefers-reduced-motion: reduce` see the static first image only.
     ========================================================================== */
  // === TUNE ME ===  rotation cadence in milliseconds
  const AUTO_ROTATE_MS = 2000;

  function initGallery() {
    const mainBox = document.querySelector('[data-hero-main]');
    if (!mainBox) return;

    const slides = Array.from(mainBox.querySelectorAll('img'));
    if (slides.length < 2) return;  // nothing to rotate

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let activeIdx = 0;
    setInterval(() => {
      const nextIdx = (activeIdx + 1) % slides.length;
      slides[activeIdx].classList.remove('is-visible');
      slides[nextIdx].classList.add('is-visible');
      activeIdx = nextIdx;
    }, AUTO_ROTATE_MS);
  }


  /* ==========================================================================
     Tap-to-play 9:16 video rail, replaces the old 3-step ritual.
     ==========================================================================
     Cards do NOT autoplay. On tap:
       1. Pause every other card and remove .is-playing
       2. Seek this video to its data-start (default 0)
       3. Unmute and play
       4. On timeupdate, pause when data-end is reached (if set)
       5. On pause/ended, drop .is-playing so the play overlay returns
     Tapping a card that's currently playing toggles it off (pauses).
     ========================================================================== */
  function initVideoRail() {
    const cards = Array.from(document.querySelectorAll('[data-video-card]'));
    if (!cards.length) return;

    function pauseAllExcept(except) {
      cards.forEach((card) => {
        if (card === except) return;
        const v = card.querySelector('[data-video-clip]');
        if (v && !v.paused) v.pause();
        card.classList.remove('is-playing');
      });
    }

    cards.forEach((card) => {
      const video = card.querySelector('[data-video-clip]');
      if (!video) return;

      const start = parseFloat(video.dataset.start) || 0;
      const end = video.dataset.end ? parseFloat(video.dataset.end) : null;

      // Seek-to-start the first time the user taps. Repeated taps after the
      // video has played past start just resume from current position.
      let hasStarted = false;

      function play() {
        pauseAllExcept(card);
        if (!hasStarted || video.currentTime < start || (end && video.currentTime >= end)) {
          try { video.currentTime = start; } catch (e) { /* ignore, some codecs balk on seek */ }
        }
        hasStarted = true;
        video.muted = false;     // sound ON per spec
        video.volume = 1;
        const p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            // Autoplay/play() rejection, drop the playing state so the
            // overlay returns and the user can try again.
            card.classList.remove('is-playing');
          });
        }
        card.classList.add('is-playing');
      }

      // Click on the card or the play button both trigger play/pause toggle.
      // Stop propagation from the button so clicking it doesn't also fire
      // the card-level handler.
      card.addEventListener('click', () => {
        if (video.paused) play();
        else video.pause();
      });

      // Enforce end-of-trim constraint
      video.addEventListener('timeupdate', () => {
        if (end !== null && video.currentTime >= end) {
          video.pause();
          card.classList.remove('is-playing');
        }
      });
      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
      });
      video.addEventListener('ended', () => {
        card.classList.remove('is-playing');
      });
    });
  }


  /* ==========================================================================
     Bundle picker, radio selection updates ATC button price + sticky bar
     ==========================================================================
     Each pack has its own inline Shipping Protection checkbox (.bundle__protection)
     that is visible only when that pack is selected. Switching packs resets
     every protection checkbox to its default (checked).
     ========================================================================== */
  function initBundlePicker() {
    const radios = document.querySelectorAll('input[name="bundle"]');
    if (!radios.length) return;

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          resetProtectionDefaults();
          fireCelebration(radio);
          updatePricesFromBundle();
        }
      });
    });

    // Initial paint, no animation on first load (set isInitial=true so the
    // count-up snaps straight to value instead of rolling from 0).
    updatePricesFromBundle(true);
  }

  // Brief celebration animation on the tier the user just selected, adds
  // a one-shot class that the CSS keyframe consumes, then strips it so the
  // class can re-apply on the next change.
  function fireCelebration(radio) {
    const label = radio.closest('.bundle');
    if (!label) return;
    label.classList.remove('is-just-selected');
    // Force a reflow so the keyframe restarts even on rapid re-selects.
    void label.offsetWidth;
    label.classList.add('is-just-selected');
    setTimeout(() => label.classList.remove('is-just-selected'), 500);
  }

  // Count-up animation for the You-Save number. Reads the current value
  // from the DOM (to support smooth deltas between tier swaps), and rolls
  // to the new target over ~360ms with an ease-out curve. Honours
  // prefers-reduced-motion by snapping straight to the target.
  function animateMoneyTo(el, target, opts) {
    if (!el) return;
    const skipAnim = !!(opts && opts.skipAnim) ||
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const fmt = (n) => '$' + n.toFixed(2);
    if (skipAnim) {
      el.textContent = fmt(target);
      return;
    }
    const startText = (el.textContent || '$0').replace(/[^0-9.\-]/g, '');
    const start = parseFloat(startText) || 0;
    if (Math.abs(start - target) < 0.01) {
      el.textContent = fmt(target);
      return;
    }
    const duration = 360;
    const startTime = performance.now();
    // Cancel any in-flight roll on this element before kicking a new one off.
    if (el._raf) cancelAnimationFrame(el._raf);
    function step(now) {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const value = start + (target - start) * eased;
      el.textContent = fmt(value);
      if (t < 1) {
        el._raf = requestAnimationFrame(step);
      } else {
        el._raf = null;
        el.textContent = fmt(target);
      }
    }
    el._raf = requestAnimationFrame(step);
    // Small pulse on every animated update.
    el.classList.remove('is-pulse');
    void el.offsetWidth;
    el.classList.add('is-pulse');
    setTimeout(() => el.classList.remove('is-pulse'), 400);
  }

  function resetProtectionDefaults() {
    document.querySelectorAll('.bundle__protection-cb').forEach((cb) => {
      cb.checked = true;
    });
  }

  function getSelectedBundle() {
    const checked = document.querySelector('input[name="bundle"]:checked');
    if (!checked) return null;
    const titleEl = checked.parentElement.querySelector('.bundle__title');
    // Use just the first text node so the "% OFF" badge text isn't included
    const label = titleEl ? (titleEl.childNodes[0]?.textContent || '').trim() : '';
    const subPriceRaw = parseFloat(checked.dataset.subPrice);
    const price = parseFloat(checked.dataset.price);
    return {
      variantId: checked.dataset.variantId,
      price,
      compare: parseFloat(checked.dataset.compare),
      sticks: parseInt(checked.dataset.sticks, 10) || 1,
      tier: parseInt(checked.dataset.tier, 10) || 1,
      savings: parseFloat(checked.dataset.savings) || (parseFloat(checked.dataset.compare) - price),
      perStick: parseFloat(checked.dataset.perStick) || (price / (parseInt(checked.dataset.sticks, 10) || 1)),
      sellingPlan: checked.dataset.sellingPlan || '',
      subPrice: isNaN(subPriceRaw) ? price * 0.85 : subPriceRaw,
      cadence: checked.dataset.cadence || '',
      label,
    };
  }

  // Returns 'subscribe' when the Subscribe radio in [data-purchase-type] is
  // checked, otherwise 'one-time' (the default selection on page load).
  function getSelectedPurchaseType() {
    const picked = document.querySelector('[data-purchase-type] input[name="purchase_type"]:checked');
    return picked && picked.value === 'subscribe' ? 'subscribe' : 'one-time';
  }

  function updatePricesFromBundle(isInitial) {
    const sel = getSelectedBundle();
    if (!sel) return;

    // Purchase path is driven by the .subscribe-toggle radios. One-time is the
    // default-checked option on page load, so the price ladder, ATC label
    // and "You Save" math all read from sel.price unless the user actively
    // flips Subscribe on. When Subscribe is active, bundlePrice swaps to
    // sel.subPrice (Shopify's selling_plan_allocations.per_delivery_price
    // already in dollars; falls back to price * 0.85 only if Shopify
    // exposed no plan for that variant).
    const mode = getSelectedPurchaseType();
    const isSub = mode === 'subscribe';
    const protectionAdd = isProtectionCheckedForSelected() ? 7.00 : 0;
    const bundlePrice = isSub ? sel.subPrice : sel.price;
    const total = bundlePrice + protectionAdd;
    const fmt = (n) => '$' + n.toFixed(2);

    // Sub strip, slim non-clickable bar above the ATC. Same data hooks the
    // old toggle's subline used ([data-sub-line] + child [data-sub-*]), so
    // the existing update logic continues to apply. Scope lookups to
    // [data-sub-line] so they don't collide with [data-sub-price] on each
    // pack radio.
    const subLine = document.querySelector('[data-sub-line]');
    if (subLine) {
      const subSticksEl = subLine.querySelector('[data-sub-sticks]');
      const subCadenceEl = subLine.querySelector('[data-sub-cadence]');
      const subPriceEl = subLine.querySelector('[data-sub-price]');
      if (subSticksEl) subSticksEl.textContent = sel.sticks === 1 ? '1 stick' : sel.sticks + ' sticks';
      if (subCadenceEl) subCadenceEl.textContent = sel.cadence || '';
      if (subPriceEl) subPriceEl.textContent = sel.subPrice.toFixed(2);
    }

    const atcPrice = document.querySelector('[data-atc-price]');
    if (atcPrice) atcPrice.textContent = fmt(total);

    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickyCompare = document.querySelector('[data-sticky-compare]');
    const stickySave = document.querySelector('[data-sticky-save]');
    const stickyPack = document.querySelector('[data-sticky-pack]');

    if (stickyPrice) stickyPrice.textContent = fmt(total);
    if (stickyCompare) stickyCompare.textContent = fmt(sel.compare);

    const savePct = Math.round(((sel.compare - sel.price) / sel.compare) * 100);
    if (stickySave) stickySave.textContent = savePct > 0 ? 'SAVE ' + savePct + '%' : '';

    if (stickyPack) {
      // Show stick count in the sticky bar (e.g. "6 Sticks")
      stickyPack.textContent = sel.sticks === 1 ? '1 Stick' : sel.sticks + ' Sticks';
    }

    // ===== Pack escalation dashboard =====
    // Total savings = pack savings vs compare-at + the recurring discount
    // (price - subPrice) ONLY when Subscribe is the active path. One-time
    // selection collapses the second term so the You Save number reads
    // honestly. Shipping Protection is never counted as "savings".
    const totalSavings = sel.savings + (isSub ? (sel.price - sel.subPrice) : 0);
    const effectivePerStick = bundlePrice / sel.sticks;

    const saveAmountEl = document.querySelector('[data-save-amount]');
    const perStickEl = document.querySelector('[data-per-stick]');
    const segEls = document.querySelectorAll('.pack-stats__seg');

    animateMoneyTo(saveAmountEl, totalSavings, { skipAnim: isInitial });
    animateMoneyTo(perStickEl, effectivePerStick, { skipAnim: isInitial });

    // 4-segment meter, segments 1..tier are lit; tier 4 lights all 4 with
    // a deeper accent on the final segment so "BEST VALUE" reads as maxed.
    segEls.forEach((seg, idx) => {
      const segNum = idx + 1;
      seg.classList.remove('is-lit', 'is-lit-strong');
      if (segNum <= sel.tier) {
        seg.classList.add(sel.tier === 4 && segNum === 4 ? 'is-lit-strong' : 'is-lit');
      }
    });

    // Gold reward states. Two independent class toggles on .conversion;
    // EITHER triggers the You Save bar to paint gold, neither leaves it teal:
    //   is-tier-4-active     : Best Value tier card selected
    //   is-subscribe-active  : Subscribe & Save option selected in the
    //                          .subscribe-toggle below the ATC
    const conversion = document.querySelector('.conversion');
    if (conversion) {
      conversion.classList.toggle('is-tier-4-active', sel.tier === 4);
      conversion.classList.toggle('is-subscribe-active', isSub);
    }
  }


  /* ==========================================================================
     Bundle protection, inline $7 Shipping Protection per pack
     ==========================================================================
     Each pack has its own .bundle__protection-row inside its .bundle__box.
     Only the row inside the currently-selected pack is visible (CSS rule).
     Clicking the row toggles its checkbox and recalculates the total.
     We stop propagation so the outer <label class="bundle"> doesn't re-fire
     its bundle radio when the user is interacting with the protection row.
     ========================================================================== */
  function initBundleProtection() {
    document.querySelectorAll('.bundle__protection-row').forEach((row) => {
      const cb = row.querySelector('input[type="checkbox"]');
      if (!cb) return;
      row.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        cb.checked = !cb.checked;
        updatePricesFromBundle();
      });
      cb.addEventListener('change', updatePricesFromBundle);
    });

    // Purchase-type radio change handling lives in initPurchaseType() below;
    // protection rows don't need to react to it directly because every
    // recompute path lands in updatePricesFromBundle().
  }


  /* ==========================================================================
     Purchase-type toggle, one-time vs subscribe
     ==========================================================================
     Two-box selector below the ATC. Buy once is the default-checked option
     so the cart payload contains NO selling_plan unless the user actively
     flips Subscribe on. On every change we:
       1. Recompute prices / "You Save" / per-stick via updatePricesFromBundle
          (it reads getSelectedPurchaseType() to choose price + savings math).
       2. updatePricesFromBundle also toggles .is-subscribe-active on the
          parent .conversion, which paints the You Save bar gold AND turns
          the Subscribe box itself gold via the CSS rules in hematite-styles.css.
     ========================================================================== */
  function initPurchaseType() {
    const radios = document.querySelectorAll('[data-purchase-type] input[name="purchase_type"]');
    if (!radios.length) return;
    radios.forEach((r) => {
      r.addEventListener('change', () => updatePricesFromBundle());
    });
  }

  function isProtectionCheckedForSelected() {
    const checked = document.querySelector('input[name="bundle"]:checked');
    if (!checked) return false;
    const cb = checked.closest('.bundle')?.querySelector('.bundle__protection-cb');
    return cb?.checked === true;
  }

  // Cart payload for ATC, bundle variant + a single shipping-protection line
  // if its checkbox is currently ticked. Returns the numeric Shopify variant
  // id from the active pack's protection row (data-upsell-id rendered by
  // Liquid from all_products['shipping-protection'].variants.first.id).
  function getActiveBumpsPayload() {
    if (!isProtectionCheckedForSelected()) return [];
    const checked = document.querySelector('input[name="bundle"]:checked');
    const row = checked?.closest('.bundle')?.querySelector('[data-upsell-id]');
    const id = row?.dataset?.upsellId;
    return id ? [id] : [];
  }


  /* ==========================================================================
     Live orders, increments by +1 or +2 every 30s for "social proof"
     ========================================================================== */
  function initLiveOrders() {
    const counter = document.querySelector('[data-order-count]');
    if (!counter) return;

    setInterval(() => {
      const current = parseInt(counter.textContent, 10) || 47;
      const bump = Math.random() < 0.5 ? 1 : 2;
      counter.textContent = String(current + bump);
    }, 30000);
  }


  /* ==========================================================================
     Reviews link, smooth scroll to #reviews
     ========================================================================== */
  function initReviewsScrollLink() {
    const link = document.querySelector('[data-scroll-to]');
    if (!link) return;

    link.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(link.dataset.scrollTo);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }


  /* ==========================================================================
     Accordions (Trust + FAQ), single shared expand/collapse helper
     ========================================================================== */
  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach((accordion) => {
      const heads = accordion.querySelectorAll('.accordion__head');
      heads.forEach((head) => {
        head.addEventListener('click', () => {
          const isOpen = head.getAttribute('aria-expanded') === 'true';

          // Close all in this accordion
          heads.forEach((h) => {
            h.setAttribute('aria-expanded', 'false');
            const body = h.nextElementSibling;
            if (body) body.style.maxHeight = '0px';
          });

          // Open this one
          if (!isOpen) {
            head.setAttribute('aria-expanded', 'true');
            const body = head.nextElementSibling;
            if (body) body.style.maxHeight = body.scrollHeight + 'px';
          }
        });
      });
    });
  }


  /* ==========================================================================
     Delivery dates, populate today / +1 dynamically; delivered step is
     a static "5-8 BUSINESS DAYS" label (store policy, not a calculated
     window). Uses querySelectorAll so the original delivery section AND
     the compact info block below the ATC both get populated.
     ========================================================================== */
  function initDeliveryDates() {
    const todayEls = document.querySelectorAll('[data-delivery-date="today"]');
    const readyEls = document.querySelectorAll('[data-delivery-date="ready"]');
    const deliveredEls = document.querySelectorAll('[data-delivery-date="delivered"]');
    if (!todayEls.length || !readyEls.length || !deliveredEls.length) return;

    const now = new Date();
    const todayStr = formatDate(now).toUpperCase();
    const readyStr = formatDate(addDays(now, 1)).toUpperCase();
    const deliveredStr = '5-8 BUSINESS DAYS';

    todayEls.forEach((el) => { el.textContent = todayStr; });
    readyEls.forEach((el) => { el.textContent = readyStr; });
    deliveredEls.forEach((el) => { el.textContent = deliveredStr; });
  }

  function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  }

  function formatDate(d) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const day = d.getDate();
    return months[d.getMonth()] + ' ' + day + ordinal(day);
  }

  function ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }


  /* ==========================================================================
     Reviews carousel, Swiper.js init
     ========================================================================== */
  function initReviewsCarousel() {
    if (typeof Swiper === 'undefined') return;
    const el = document.querySelector('.reviews-swiper');
    if (!el) return;

    new Swiper(el, {
      slidesPerView: 1,
      spaceBetween: 20,
      loop: true,
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      pagination: {
        el: el.querySelector('.swiper-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
    });
  }


  /* ==========================================================================
     Sticky bottom ATC, scroll-position trigger
     ==========================================================================
     Show the sticky cart bar only when the user has scrolled DOWN to the
     9:16 video section (or below it). The moment they scroll back ABOVE
     the video section, the bar drops out instantly. The instant-drop is
     handled by CSS: the transform transition only fires when .is-visible
     is added, not removed (see hematite-styles.css SECTION 11).
     ========================================================================== */
  function initStickyATC() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const trigger = document.querySelector('[data-video-section]');
    const mainAtc = document.querySelector('[data-atc-btn]');
    if (!sticky || !trigger) return;

    let ticking = false;
    let isVisible = false;

    function update() {
      ticking = false;
      // Use bounding rect's top: when it's <= 0, the section's top edge has
      // crossed the viewport top, user is AT or BELOW the video section.
      // We don't care whether the section is in view; we care whether the
      // user is past its top. This makes the trigger position-based rather
      // than visibility-based.
      const sectionTop = trigger.getBoundingClientRect().top;
      const shouldShow = sectionTop <= 0;
      if (shouldShow !== isVisible) {
        isVisible = shouldShow;
        sticky.classList.toggle('is-visible', shouldShow);
        sticky.setAttribute('aria-hidden', String(!shouldShow));
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Initial check, set state right on load in case the page is opened
    // already scrolled down (refresh, anchor link, browser-restored scroll).
    update();

    // Sticky ATC button → trigger main ATC submit
    const stickyBtn = document.querySelector('[data-sticky-add]');
    if (stickyBtn && mainAtc) {
      stickyBtn.addEventListener('click', () => {
        mainAtc.click();
      });
    }
  }


  /* ==========================================================================
     Scroll-to-top button, appears after 500px
     ========================================================================== */
  function initScrollTop() {
    const btn = document.querySelector('[data-scroll-top]');
    if (!btn) return;

    const threshold = 500;
    let ticking = false;

    function check() {
      if (window.scrollY > threshold) {
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
      }
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(check);
          ticking = true;
        }
      },
      { passive: true }
    );

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }


  /* ==========================================================================
     ATC submit handler
     ==========================================================================
     Posts {id, quantity[, selling_plan]} for the selected variant. The
     selling_plan key is only attached when the user has actively chosen
     Subscribe in the .subscribe-toggle below the ATC; the default is Buy
     once, so the default payload is one-time with no recurring enrolment.

     If Subscribe is on but the variant somehow exposes no selling_plan,
     we fall through to a one-time submit and log a console warning so
     the breakage surfaces (rather than a silent broken cart). The
     defence-in-depth `selling_plan` key check below catches any future
     bug that would leak a plan into a one-time payload.

     Shipping-protection upsell lines from getActiveBumpsPayload() are
     always submitted as one-time line items, matching the pre-existing
     behaviour.
     ========================================================================== */
  function initATC() {
    const form = document.querySelector('[data-atc-form]');
    const btn = document.querySelector('[data-atc-btn]');
    if (!form || !btn) return;

    const initialBtnHtml = btn.innerHTML;

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      btn.innerHTML = initialBtnHtml;
      btn.disabled = false;
      btn.classList.remove('is-pulsing');
      updatePricesFromBundle();
    });

    // Builds the /cart/add.js items array.
    //   mode === 'subscribe' → attach selling_plan when the variant has one.
    //   mode === 'one-time'  → NEVER attach selling_plan.
    // Upsells (Shipping Protection) are always one-time line items regardless
    // of mode, matching the pre-existing behaviour.
    function buildCartItems(sel, mode) {
      const bundleItem = { id: parseInt(sel.variantId, 10), quantity: 1 };
      if (mode === 'subscribe') {
        if (sel.sellingPlan) {
          const planId = parseInt(sel.sellingPlan, 10);
          if (planId) {
            bundleItem.selling_plan = planId;
          } else {
            console.warn('[ATC] selling_plan id present but not numeric, submitting as one-time:', sel.sellingPlan);
          }
        } else {
          console.warn('[ATC] no selling_plan exposed for variant ' + sel.variantId + '; submitting as one-time. Check Shopify Subscriptions admin.');
        }
      }
      const items = [bundleItem];
      getActiveBumpsPayload().forEach((id) => {
        const numericId = parseInt(id, 10);
        if (numericId) items.push({ id: numericId, quantity: 1 });
      });
      return items;
    }

    async function submitCart(items, triggerEl) {
      const wasBtn = triggerEl === btn;
      const originalText = wasBtn ? btn.innerHTML : null;
      if (wasBtn) {
        btn.classList.add('is-pulsing');
        btn.disabled = true;
        btn.textContent = 'Adding...';
      } else if (triggerEl) {
        triggerEl.classList.add('is-pending');
        triggerEl.setAttribute('aria-disabled', 'true');
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || ('HTTP ' + res.status));
        }
        syncCartCount();
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[ATC] /cart/add.js failed:', err);
        if (wasBtn) {
          btn.disabled = false;
          btn.innerHTML = originalText;
          btn.classList.remove('is-pulsing');
        } else if (triggerEl) {
          triggerEl.classList.remove('is-pending');
          triggerEl.removeAttribute('aria-disabled');
        }
        alert('Could not add to cart: ' + err.message);
      }
    }

    // Primary ATC. Reads the .subscribe-toggle for the active mode and
    // builds the cart payload accordingly. One-time is the default mode
    // (set in HTML via `checked` on the Buy-once radio).
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const sel = getSelectedBundle();
      if (!sel) return;
      const mode = getSelectedPurchaseType();
      const items = buildCartItems(sel, mode);
      // Defence-in-depth: a one-time submit must NEVER carry selling_plan.
      // If buildCartItems is ever edited in a way that leaks a plan into
      // the one-time payload, abort the request before it hits Shopify.
      if (mode === 'one-time' && items[0] && 'selling_plan' in items[0]) {
        console.error('[ATC] selling_plan leaked into one-time payload, aborting');
        return;
      }
      submitCart(items, btn);
    });
  }

  function bumpCartCount(by) {
    const badge = document.querySelector('[data-cart-count]');
    if (!badge) return;
    const current = parseInt(badge.textContent, 10) || 0;
    badge.textContent = String(current + by);
  }

  /* ==========================================================================
     Cart-count badge: re-sync from the real cart
     ==========================================================================
     Header badge is server-rendered with {{ cart.item_count }}. Goes stale
     after a cart mutation or bfcache restore. /cart.js is the source of truth.
     ========================================================================== */
  function syncCartCount() {
    const badge = document.querySelector('[data-cart-count]');
    if (!badge) return;
    fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then((res) => res.json())
      .then((cart) => {
        if (cart && typeof cart.item_count === 'number') {
          badge.textContent = String(cart.item_count);
        }
      })
      .catch(() => {});
  }

  function initCartCountSync() {
    if (!document.querySelector('[data-cart-count]')) return;
    window.addEventListener('pageshow', syncCartCount);
  }


  /* ==========================================================================
     Newsletter, prevent default submit (prototype only)
     ========================================================================== */
  function initNewsletter() {
    const form = document.querySelector('[data-newsletter]');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value) return;
      console.log('[Newsletter] Subscribed:', input.value);
      const btn = form.querySelector('button');
      if (btn) {
        const original = btn.textContent;
        btn.textContent = 'JOINED';
        setTimeout(() => { btn.textContent = original; }, 2000);
      }
      input.value = '';
    });
  }


  /* ==========================================================================
     "What are you using right now?", three problem cards
     ==========================================================================
     One-open-at-a-time. Clicking the active card again closes it. Pure DOM
     toggling, no animation library, and aria-selected / aria-expanded are
     kept in sync for screen readers.
     ========================================================================== */
  function initProblemSection() {
    const cards  = document.querySelectorAll('.problem__card');
    const panels = document.querySelectorAll('.problem__panel');
    if (!cards.length || !panels.length) return;

    function close(card) {
      card.setAttribute('aria-selected', 'false');
    }
    function open(card) {
      card.setAttribute('aria-selected', 'true');
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const target = card.dataset.problem;
        const isOpen = card.getAttribute('aria-selected') === 'true';

        // Reset all
        cards.forEach(close);
        panels.forEach((p) => { p.hidden = true; });

        // If this card was closed, open it now
        if (!isOpen) {
          open(card);
          const panel = document.querySelector(`[data-problem-panel="${target}"]`);
          if (panel) panel.hidden = false;
        }
      });
    });
  }


  /* ==========================================================================
     Flash-sale countdown bar (above the pack selector)
     ==========================================================================
     Counts down from 5:00. On reaching 0:00 the label flips to "LAST CHANCE
    , OFFER ENDING", the timer loops back to 5:00, and counting continues
     forever from there. The reset is deliberately disguised as escalating
     urgency. Resets to "FLASH SALE" 5:00 on every fresh page load.
     ========================================================================== */
  function initFlashBar() {
    const bar      = document.querySelector('[data-flash-bar]');
    const labelEl  = document.querySelector('[data-flash-label]');
    const timerEl  = document.querySelector('[data-flash-timer]');
    if (!bar || !labelEl || !timerEl) return;

    const RESET_SECONDS = 5 * 60;   // 5:00
    let remaining = RESET_SECONDS;
    let lastChance = false;

    function render() {
      const s = Math.max(0, remaining);
      const mm = Math.floor(s / 60);
      const ss = s % 60;
      timerEl.textContent = `${mm}:${ss.toString().padStart(2, '0')}`;
    }

    function flipToLastChance() {
      lastChance = true;
      labelEl.textContent = 'LAST CHANCE · OFFER ENDING';
      bar.dataset.mode = 'last-chance';
    }

    render();
    setInterval(() => {
      remaining -= 1;
      if (remaining < 0) {
        if (!lastChance) flipToLastChance();
        remaining = RESET_SECONDS;
      }
      render();
    }, 1000);
  }


  /* ==========================================================================
     Scratch-card popup
     ==========================================================================
     Four steps: scratch → email → phone (with skip) → code reveal.
     A brushed-silver canvas overlays a hidden 10% OFF prize; once ~60% of
     the surface is cleared, the canvas auto-completes and the email-capture
     step shows. Email submit advances to phone capture (skippable), then
     reveals the actual code (IRON10) and sets a localStorage flag so the
     popup never shows again on this device.

     KLAVIYO WIRING: deferred. Email + phone fields are validated but the
     values are not POSTed anywhere — the discount code is still granted on
     submit. Once Jack provisions Klaviyo (after 5–10 orders/day), wire
     fetch() calls into the email + phone submit handlers below; the steps
     and validation are already correct.

     Step transition is via [data-scratch-step="N"] + .is-active class on
     each step panel. STORAGE_KEY uses 'hematite_' prefix so the Siwak
     clone history doesn't suppress the popup.
     ========================================================================== */
  function initScratchPopup() {
    const STORAGE_KEY = 'hematite_scratch_claimed';
    const AUTO_SHOW_DELAY_MS = 5000;
    const SCRATCH_THRESHOLD = 0.60;   // 60% of pixels erased triggers auto-reveal
    const ERASE_RADIUS = 22;          // px (in CSS space) per scratch dab
    const DISCOUNT_CODE = 'IRON10';

    const popup = document.querySelector('[data-scratch]');
    if (!popup) return;

    // Persistent "don't show again once completed"
    if (safeGetItem(STORAGE_KEY) === 'true') return;

    const canvas    = popup.querySelector('[data-scratch-canvas]');
    const closeBtns = popup.querySelectorAll('[data-scratch-close]');

    // 4-step DOM bindings (HTML structure: snippets/hematite-scratch-popup.liquid)
    const steps        = popup.querySelectorAll('[data-scratch-step]');
    const emailForm    = popup.querySelector('[data-scratch-email-form]');
    const emailInput   = popup.querySelector('[data-scratch-email-input]');
    const emailError   = popup.querySelector('[data-scratch-email-error]');
    const phoneForm    = popup.querySelector('[data-scratch-phone-form]');
    const phoneInput   = popup.querySelector('[data-scratch-phone-input]');
    const phoneError   = popup.querySelector('[data-scratch-phone-error]');
    const skipPhoneBtn = popup.querySelector('[data-scratch-skip-phone]');
    const codeEl       = popup.querySelector('[data-scratch-code-value]');
    const copyBtn      = popup.querySelector('[data-scratch-copy]');
    const copyLabel    = popup.querySelector('[data-scratch-copy-label]');
    const shopNowBtn   = popup.querySelector('[data-scratch-shop-now]');

    let scratching = false;
    let scratchTriggered = false;
    let lastPercentCheck = 0;
    let canvasReady = false;
    // Previous pointer position within the active stroke. Used to draw a
    // continuous line between consecutive move events instead of stamping
    // isolated dots with gaps. Reset on pointer-up.
    let lastX = null;
    let lastY = null;

    /* --- Canvas init: brushed-silver fill + striping, then erase mode --- */
    function setupCanvas() {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      const dpr = window.devicePixelRatio || 1;
      canvas.width  = Math.floor(rect.width  * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Brushed-silver gradient
      const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
      grad.addColorStop(0.00, '#b8bcc4');
      grad.addColorStop(0.25, '#d9dce1');
      grad.addColorStop(0.50, '#a8acb3');
      grad.addColorStop(0.75, '#d1d4d9');
      grad.addColorStop(1.00, '#9ea2a8');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Brushed-metal horizontal striping
      ctx.globalAlpha = 0.18;
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#7a7d83';
      for (let i = 0; i < 90; i++) {
        const y = Math.random() * rect.height;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(rect.width, y + (Math.random() * 3 - 1.5));
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // Centered "SCRATCH HERE" hint
      ctx.font = '800 13px Inter, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(40, 40, 40, 0.55)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('SCRATCH HERE', rect.width / 2, rect.height / 2);

      // Switch context to erase mode for subsequent scratch strokes
      ctx.globalCompositeOperation = 'destination-out';
      // Smooth-stroke setup: erase as a continuous round-capped line, not
      // a series of discrete dots. Width matches the dot radius * 2 so
      // single-tap dot + drag-line erase at the same thickness.
      ctx.lineWidth = ERASE_RADIUS * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      canvasReady = true;
      return true;
    }

    function getPos(e) {
      const rect = canvas.getBoundingClientRect();
      const p = e.touches && e.touches[0] ? e.touches[0] : e;
      return { x: p.clientX - rect.left, y: p.clientY - rect.top };
    }

    // Dot eraser, used on initial tap and as a fallback when there's no
    // previous position. Stamps a single round footprint.
    function eraseDot(x, y) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(x, y, ERASE_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }

    // Line eraser, used during drag. Draws a continuous round-capped line
    // from the last pointer position to the current one, eliminating the
    // visible-gap-between-circles look that fast swipes produced before.
    function eraseLineTo(x, y) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    function onStart(e) {
      if (!canvasReady || scratchTriggered) return;
      e.preventDefault();
      scratching = true;
      const p = getPos(e);
      eraseDot(p.x, p.y);
      lastX = p.x;
      lastY = p.y;
    }
    function onMove(e) {
      if (!scratching || scratchTriggered) return;
      e.preventDefault();
      const p = getPos(e);
      if (lastX !== null && lastY !== null) {
        eraseLineTo(p.x, p.y);
      } else {
        eraseDot(p.x, p.y);
      }
      lastX = p.x;
      lastY = p.y;

      // Throttle the percent-erased check; it's a getImageData scan.
      const now = Date.now();
      if (now - lastPercentCheck > 120) {
        lastPercentCheck = now;
        if (computeErasedPercent() >= SCRATCH_THRESHOLD) {
          scratchTriggered = true;
          scratching = false;
          autoClearAndReveal();
        }
      }
    }
    function onEnd() {
      scratching = false;
      // Reset stroke state so the next touch starts cleanly with a dot
      // instead of drawing a line back across the canvas from where the
      // previous stroke ended.
      lastX = null;
      lastY = null;
    }

    function computeErasedPercent() {
      const ctx = canvas.getContext('2d');
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let cleared = 0;
      let total = 0;
      // Sample every 16th pixel for performance, accurate enough at 55% threshold
      for (let i = 3; i < data.length; i += 4 * 16) {
        total++;
        if (data[i] < 32) cleared++;
      }
      return total === 0 ? 0 : cleared / total;
    }

    function autoClearAndReveal() {
      // Fade the canvas out, then clear and switch to step 2 (email)
      let alpha = 1;
      const tick = () => {
        alpha -= 0.08;
        if (alpha <= 0) {
          const ctx = canvas.getContext('2d');
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          canvas.style.opacity = '';
          setTimeout(() => showStep(2), 220);
          return;
        }
        canvas.style.opacity = String(alpha);
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    /* --- Step transitions ---------------------------------------------- */
    function showStep(stepNumber) {
      steps.forEach((panel) => {
        const isActive = String(panel.dataset.scratchStep) === String(stepNumber);
        panel.classList.toggle('is-active', isActive);
      });
      // Focus the input on email + phone steps for snappier UX
      if (stepNumber === 2 && emailInput) {
        setTimeout(() => { try { emailInput.focus(); } catch (e) {} }, 100);
      } else if (stepNumber === 3 && phoneInput) {
        setTimeout(() => { try { phoneInput.focus(); } catch (e) {} }, 100);
      }
    }

    /* --- Open / close -------------------------------------------------- */
    function openPopup() {
      popup.hidden = false;
      // Defer canvas sizing until the backdrop has rendered and the wrap
      // has a measurable bounding rect.
      requestAnimationFrame(() => {
        if (!setupCanvas()) {
          // Retry once if the first measurement failed (rare layout race)
          requestAnimationFrame(setupCanvas);
        }
      });
    }
    function closePopup() {
      popup.hidden = true;
    }

    /* --- Validation ---------------------------------------------------- */
    function isValidEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
    }
    function isValidPhone(v) {
      // Loose intl-friendly check: 8+ digits, optional + and spaces/dashes
      const digits = String(v).replace(/\D/g, '');
      return digits.length >= 8 && digits.length <= 15;
    }
    function showError(el, on) {
      if (!el) return;
      if (on) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    }

    /* --- Step 2: email submit ------------------------------------------ */
    if (emailForm) {
      emailForm.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!emailInput) return;
        const value = emailInput.value;
        if (!isValidEmail(value)) {
          emailInput.setAttribute('aria-invalid', 'true');
          showError(emailError, true);
          emailInput.focus();
          return;
        }
        emailInput.removeAttribute('aria-invalid');
        showError(emailError, false);
        // KLAVIYO STUB: deferred until Jack provisions Klaviyo at scale.
        // No POST. Still advance the flow + grant the code.
        showStep(3);
      });
    }

    /* --- Step 3: phone submit ------------------------------------------ */
    if (phoneForm) {
      phoneForm.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!phoneInput) return;
        const value = phoneInput.value;
        if (!isValidPhone(value)) {
          phoneInput.setAttribute('aria-invalid', 'true');
          showError(phoneError, true);
          phoneInput.focus();
          return;
        }
        phoneInput.removeAttribute('aria-invalid');
        showError(phoneError, false);
        // KLAVIYO STUB: deferred. Still advance to the code reveal.
        finishWithCode();
      });
    }

    /* --- Step 3: skip phone -------------------------------------------- */
    if (skipPhoneBtn) {
      skipPhoneBtn.addEventListener('click', (e) => {
        e.preventDefault();
        finishWithCode();
      });
    }

    function finishWithCode() {
      showStep(4);
      safeSetItem(STORAGE_KEY, 'true');
    }

    /* --- Step 4: copy code --------------------------------------------- */
    if (copyBtn && codeEl) {
      copyBtn.addEventListener('click', () => {
        const code = codeEl.textContent.trim() || DISCOUNT_CODE;
        if (navigator.clipboard) navigator.clipboard.writeText(code).catch(() => {});
        const original = copyLabel ? copyLabel.textContent : copyBtn.textContent;
        if (copyLabel) copyLabel.textContent = 'COPIED';
        else copyBtn.textContent = 'COPIED';
        setTimeout(() => {
          if (copyLabel) copyLabel.textContent = original;
          else copyBtn.textContent = original;
        }, 1500);
      });
    }

    /* --- Step 4: shop now (auto-apply discount via /discount/ URL) ----- */
    if (shopNowBtn) {
      shopNowBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const code = (codeEl && codeEl.textContent.trim()) || DISCOUNT_CODE;
        window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/products/hematite-band';
      });
    }

    /* --- Close wiring -------------------------------------------------- */
    closeBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closePopup();
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !popup.hidden) closePopup();
    });


    /* --- Scratch listeners (mouse + touch) ---------------------------- */
    canvas.addEventListener('mousedown',  onStart);
    canvas.addEventListener('mousemove',  onMove);
    canvas.addEventListener('mouseup',    onEnd);
    canvas.addEventListener('mouseleave', onEnd);
    canvas.addEventListener('touchstart', onStart, { passive: false });
    canvas.addEventListener('touchmove',  onMove,  { passive: false });
    canvas.addEventListener('touchend',   onEnd);
    canvas.addEventListener('touchcancel', onEnd);

    /* --- localStorage helpers (safe in private-mode browsers) --------- */
    function safeGetItem(key) {
      try { return window.localStorage.getItem(key); }
      catch (e) { return null; }
    }
    function safeSetItem(key, value) {
      try { window.localStorage.setItem(key, value); }
      catch (e) { /* fall through */ }
    }

    /* --- Boot ---------------------------------------------------------- */
    setTimeout(openPopup, AUTO_SHOW_DELAY_MS);
  }

})();


/* ---------------------------------------------------------------------
   Before/After BBQ slider, drag the divider to wipe dirty/clean
   --------------------------------------------------------------------- */
(() => {
  const frame = document.querySelector('[data-ba-frame]');
  if (!frame) return;
  const handle = frame.querySelector('.ba__handle');
  let dragging = false;

  const setPos = (clientX) => {
    const rect = frame.getBoundingClientRect();
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    frame.style.setProperty('--pos', pct.toFixed(2) + '%');
  };

  const start = (e) => {
    dragging = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setPos(x);
    e.preventDefault();
  };
  const move = (e) => {
    if (!dragging) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setPos(x);
  };
  const end = () => { dragging = false; };

  frame.addEventListener('mousedown', start);
  document.addEventListener('mousemove', move);
  document.addEventListener('mouseup', end);
  frame.addEventListener('touchstart', start, { passive: false });
  document.addEventListener('touchmove', move, { passive: true });
  document.addEventListener('touchend', end);

  if (handle) {
    handle.addEventListener('keydown', (e) => {
      const current = parseFloat(frame.style.getPropertyValue('--pos')) || 70;
      let next = current;
      if (e.key === 'ArrowLeft') next = current - 5;
      else if (e.key === 'ArrowRight') next = current + 5;
      else return;
      next = Math.max(0, Math.min(100, next));
      frame.style.setProperty('--pos', next + '%');
      e.preventDefault();
    });
  }
})();


/* ---------------------------------------------------------------------
   Review wall, Load more: show 12 cards initially, +12 per click
   --------------------------------------------------------------------- */
(() => {
  const grid = document.querySelector('[data-review-grid]');
  const button = document.querySelector('[data-loadmore]');
  if (!grid || !button) return;

  const INITIAL = 12;
  const STEP = 12;
  const cards = Array.from(grid.querySelectorAll('.rw-card'));
  let visible = INITIAL;

  const apply = () => {
    cards.forEach((card, idx) => {
      if (idx < visible) card.removeAttribute('hidden');
      else card.setAttribute('hidden', '');
    });
    if (visible >= cards.length) button.setAttribute('hidden', '');
  };

  apply();

  button.addEventListener('click', () => {
    visible = Math.min(visible + STEP, cards.length);
    apply();
  });
})();


/* ---------------------------------------------------------------------
   Advertorial sticky CTA bar, slides up once the reader has scrolled
   past the first viewport. Hidden at the very top of the page so the
   hero gets a clean first impression. Mobile-only via CSS media query;
   JS toggles the visible class on scroll regardless of viewport.
   --------------------------------------------------------------------- */
(() => {
  const bar = document.querySelector('[data-sticky-cta]');
  if (!bar) return;

  const SHOW_AFTER = () => Math.max(320, window.innerHeight * 0.7);
  let visible = false;
  let threshold = SHOW_AFTER();

  const update = () => {
    const shouldShow = window.scrollY > threshold;
    if (shouldShow !== visible) {
      visible = shouldShow;
      bar.classList.toggle('ad-sticky--visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
  };

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', () => { threshold = SHOW_AFTER(); update(); }, { passive: true });
  update();
})();


/* ---------------------------------------------------------------------
   Bulletproof video looping. Safari sometimes drops out of <video loop>
   after a tab unfocus or several iterations; force-restart on `ended`
   and resume any paused loop videos when the tab regains focus.
   --------------------------------------------------------------------- */
(() => {
  const loops = document.querySelectorAll('video[loop]');
  if (!loops.length) return;

  loops.forEach((v) => {
    v.addEventListener('ended', () => {
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    loops.forEach((v) => {
      if (v.paused) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    });
  });
})();
